import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvidedDragHandleProps
} from "@hello-pangea/dnd";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DragDropVerticalIcon,
  Delete01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { IconButton } from "shared/components";
import {
  useSurveyStore,
  selectActiveQuestion
} from "views/landlord-surveys-id/store";
import { QuestionType } from "api/landlord-surveys/types/questions";

const OptionRow = ({
  value,
  index,
  onChange,
  onDelete,
  canDelete,
  dragHandleProps
}: {
  value: string;
  index: number;
  onChange: (value: string) => void;
  onDelete: () => void;
  canDelete: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}) => {
  return (
    <div className="flex items-center gap-2 group">
      <div className="shadow-xs h-12 flex items-center gap-3 flex-grow border border-alpha-100 rounded-lg px-3 py-2.5 bg-white">
        <input
          type="radio"
          disabled
          className="shrink-0 h-4 w-4 border-content-100 text-primary-500 focus:ring-0 focus:ring-offset-0 disabled:cursor-default"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Option ${index + 1}`}
          className="flex-grow bg-transparent border-none outline-none focus:outline-none focus:ring-0 p-0 text-sm text-content-700 placeholder:text-content-200 font-medium"
        />
        <div
          {...dragHandleProps}
          className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        >
          <HugeiconsIcon
            icon={DragDropVerticalIcon}
            size={20}
            strokeWidth={1.5}
            className="text-content-300"
          />
        </div>
      </div>

      {canDelete && (
        <IconButton
          HugeIcon={Delete01Icon}
          onClick={onDelete}
          className="shrink-0"
        />
      )}
    </div>
  );
};

export const RadioWidget = () => {
  const activeQuestion = useSurveyStore(selectActiveQuestion);
  const updateActiveQuestion = useSurveyStore(
    (state) => state.updateActiveQuestion
  );

  if (!activeQuestion || activeQuestion.type !== QuestionType.RADIO_GROUP)
    return null;

  const options = activeQuestion.options;

  const updateOptions = (
    getNewOptions: (prev: [string, ...string[]]) => [string, ...string[]]
  ) => {
    updateActiveQuestion((prev) => {
      if (prev.type !== QuestionType.RADIO_GROUP) return prev;
      return { ...prev, options: getNewOptions(prev.options) };
    });
  };

  const handleChange = (index: number, value: string) => {
    updateOptions((prev) => {
      const updated = [...prev] as [string, ...string[]];
      updated[index] = value;
      return updated;
    });
  };

  const handleDelete = (index: number) => {
    updateOptions((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index) as [string, ...string[]];
    });
  };

  const handleAdd = () => {
    updateOptions((prev) => [...prev, ""] as [string, ...string[]]);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    updateOptions((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated as [string, ...string[]];
    });
  };

  return (
    <div className="flex flex-col gap-3 mt-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="radio-options">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="flex flex-col gap-3"
            >
              {options.map((option, i) => (
                <Draggable key={i} draggableId={`radio-option-${i}`} index={i}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        transform: snapshot.isDragging
                          ? `${provided.draggableProps.style?.transform ?? ""} rotate(2deg)`
                          : provided.draggableProps.style?.transform
                      }}
                    >
                      <OptionRow
                        value={option}
                        index={i}
                        onChange={handleChange.bind(null, i)}
                        onDelete={handleDelete.bind(null, i)}
                        canDelete={options.length > 1}
                        dragHandleProps={provided.dragHandleProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <button
        type="button"
        onClick={handleAdd}
        className="text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors self-start"
      >
        Add option
      </button>
    </div>
  );
};
